import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getOAuthUrl, getEmailAccounts, deleteEmailAccount, addAlias, deleteAlias, updateAccountLimit, updateAliasLimit } from '../services/api';

function EmailAccounts() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAliasModal, setShowAliasModal] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [aliasForm, setAliasForm] = useState({ email: '', name: '' });
  const [limitForm, setLimitForm] = useState({ value: '', type: '', id: null });

  useEffect(() => {
    loadAccounts();
    
    const urlParams = new URLSearchParams(window.location.search);
    const successParam = urlParams.get('success');
    if (successParam) {
      const message = successParam === 'updated' 
        ? 'Email account updated successfully!' 
        : 'Email account connected successfully!';
      setSuccess(message);
      window.history.replaceState({}, document.title, window.location.pathname);
      setTimeout(() => {
        navigate('/dashboard?success=' + successParam, { replace: true });
      }, 2000);
    }
  }, [navigate]);

  const loadAccounts = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await getEmailAccounts();
      if (response.success) {
        setAccounts(response.accounts || []);
      } else {
        setError(response.message || 'Failed to load email accounts');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load email accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAccount = async () => {
    try {
      const response = await getOAuthUrl();
      if (response.success) {
        window.location.href = response.authUrl;
      } else {
        setError(response.message || 'Failed to initiate OAuth');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to initiate OAuth');
    }
  };

  const handleDeleteAccount = async (accountId) => {
    if (!window.confirm('Are you sure you want to remove this email account?')) return;

    try {
      const response = await deleteEmailAccount(accountId);
      if (response.success) {
        setSuccess('Email account removed successfully');
        loadAccounts();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.message || 'Failed to remove account');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to remove account');
    }
  };

  const handleAddAlias = async (e) => {
    e.preventDefault();
    if (!selectedAccount) return;

    try {
      const response = await addAlias(selectedAccount.id, aliasForm.email, aliasForm.name);
      if (response.success) {
        setSuccess('Alias added successfully');
        setShowAliasModal(false);
        setAliasForm({ email: '', name: '' });
        loadAccounts();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.message || 'Failed to add alias');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add alias');
    }
  };

  const handleDeleteAlias = async (aliasId) => {
    if (!window.confirm('Are you sure you want to remove this alias?')) return;

    try {
      const response = await deleteAlias(aliasId);
      if (response.success) {
        setSuccess('Alias removed successfully');
        loadAccounts();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.message || 'Failed to remove alias');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to remove alias');
    }
  };

  const handleUpdateLimit = async (e) => {
    e.preventDefault();
    if (!limitForm.id || !limitForm.value) return;

    try {
      const response = limitForm.type === 'account'
        ? await updateAccountLimit(limitForm.id, parseInt(limitForm.value))
        : await updateAliasLimit(limitForm.id, parseInt(limitForm.value));
      
      if (response.success) {
        setSuccess('Daily limit updated successfully');
        setShowLimitModal(false);
        setLimitForm({ value: '', type: '', id: null });
        loadAccounts();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.message || 'Failed to update limit');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update limit');
    }
  };

  const openAliasModal = (account) => {
    setSelectedAccount(account);
    setShowAliasModal(true);
  };

  const openLimitModal = (type, id, currentLimit) => {
    setLimitForm({ type, id, value: currentLimit.toString() });
    setShowLimitModal(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0e27] flex items-center justify-center relative z-10">
        <div className="text-center">
          <div className="animate-spin border-4 border-blue-500/30 border-t-blue-500 h-12 w-12 mx-auto rounded-full"></div>
          <p className="mt-4 text-gray-400">Loading email accounts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0e27] py-8 px-4 relative z-10">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8 flex justify-between items-center flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-semibold text-white mb-2">Email Accounts</h1>
            <p className="text-sm text-gray-400">Manage your Gmail accounts and aliases</p>
          </div>
          <button
            onClick={handleAddAccount}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-all shadow-lg shadow-blue-600/30"
          >
            + Add Gmail Account
          </button>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-900/20 border border-red-500/30 rounded-lg backdrop-blur-sm">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-blue-900/20 border border-blue-500/30 rounded-lg backdrop-blur-sm">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <div className="flex-1">
                <p className="text-blue-400 text-sm font-medium">{success}</p>
                <p className="text-xs text-gray-400 mt-1">Redirecting to dashboard...</p>
              </div>
            </div>
          </div>
        )}

        {accounts.length === 0 ? (
          <div className="bg-[#1a1f3a]/80 border border-blue-500/20 rounded-lg p-12 text-center backdrop-blur-sm">
            <svg className="mx-auto h-12 w-12 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <h3 className="mt-4 text-lg font-medium text-white">No email accounts</h3>
            <p className="mt-2 text-gray-400">Get started by connecting your first Gmail account</p>
            <button
              onClick={handleAddAccount}
              className="mt-6 px-6 py-3 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-all shadow-lg shadow-blue-600/30"
            >
              Connect Gmail Account
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {accounts.map((account) => (
              <div key={account.id} className="bg-[#1a1f3a]/80 border border-blue-500/20 rounded-lg backdrop-blur-sm">
                <div className="p-6 border-b border-gray-700">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-4">
                      <div className="h-12 w-12 rounded-full bg-blue-600/20 border border-blue-500/30 flex items-center justify-center">
                        <span className="text-blue-400 font-semibold text-lg">
                          {account.email_address.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-white">{account.email_address}</h3>
                        <p className="text-sm text-gray-400">
                          {account.account_name || 'Gmail Account'}
                          {account.token_valid ? (
                            <span className="ml-2 text-green-400">● Active</span>
                          ) : (
                            <span className="ml-2 text-red-400">● Token Expired</span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-sm">
                        <button
                          onClick={() => openLimitModal('account', account.id, account.daily_email_limit)}
                          className="text-blue-400 hover:text-blue-300 font-medium transition-colors"
                        >
                          *{account.daily_email_limit}/day
                        </button>
                        {account.remaining !== undefined && (
                          <span className="ml-2 text-gray-400">
                            ({account.remaining} remaining)
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => handleDeleteAccount(account.id)}
                        className="px-4 py-2 text-sm font-medium text-red-400 hover:text-red-300 border border-red-500/30 hover:border-red-500/50 rounded-lg transition-all"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>

                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-md font-semibold text-white">Aliases</h4>
                    <button
                      onClick={() => openAliasModal(account)}
                      className="px-3 py-1 text-sm text-blue-400 hover:text-blue-300 font-medium transition-colors"
                    >
                      + Add Alias
                    </button>
                  </div>

                  {account.aliases && account.aliases.length > 0 ? (
                    <div className="space-y-3">
                      {account.aliases.map((alias) => (
                        <div
                          key={alias.id}
                          className="flex items-center justify-between p-4 bg-black/50 rounded-lg border border-gray-700"
                        >
                          <div className="flex-1">
                            <p className="font-medium text-white">{alias.alias_email}</p>
                            {alias.alias_name && (
                              <p className="text-sm text-gray-400">{alias.alias_name}</p>
                            )}
                            <div className="flex items-center mt-2 gap-3">
                              {alias.is_verified ? (
                                <span className="text-xs text-green-400">✓ Verified</span>
                              ) : (
                                <span className="text-xs text-yellow-400">⚠ Unverified</span>
                              )}
                              <span className="text-xs text-gray-400">
                                *{alias.daily_email_limit}/day
                              </span>
                              {alias.remaining !== undefined && (
                                <span className="text-xs text-gray-400">
                                  ({alias.remaining} remaining)
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => openLimitModal('alias', alias.id, alias.daily_email_limit)}
                              className="px-3 py-1 text-xs text-blue-400 hover:text-blue-300 transition-colors"
                            >
                              Edit Limit
                            </button>
                            <button
                              onClick={() => handleDeleteAlias(alias.id)}
                              className="px-3 py-1 text-xs text-red-400 hover:text-red-300 transition-colors"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-400 text-sm">No aliases added yet</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add Alias Modal */}
        {showAliasModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-[#1a1f3a] border border-blue-500/30 rounded-lg p-6 w-full max-w-md shadow-xl">
              <h2 className="text-xl font-semibold text-white mb-4">Add Alias</h2>
              <form onSubmit={handleAddAlias}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Alias Email
                  </label>
                  <input
                    type="email"
                    value={aliasForm.email}
                    onChange={(e) => setAliasForm({ ...aliasForm, email: e.target.value })}
                    required
                    className="w-full px-4 py-2 bg-black/50 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500"
                    placeholder="alias@example.com"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Make sure this alias is verified in Gmail settings first
                  </p>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Display Name (Optional)
                  </label>
                  <input
                    type="text"
                    value={aliasForm.name}
                    onChange={(e) => setAliasForm({ ...aliasForm, name: e.target.value })}
                    className="w-full px-4 py-2 bg-black/50 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500"
                    placeholder="Tech Team"
                  />
                </div>
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAliasModal(false);
                      setAliasForm({ email: '', name: '' });
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 rounded-lg transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-all"
                  >
                    Add Alias
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Update Limit Modal */}
        {showLimitModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-[#1a1f3a] border border-blue-500/30 rounded-lg p-6 w-full max-w-md shadow-xl">
              <h2 className="text-xl font-semibold text-white mb-4">Update Daily Limit</h2>
              <form onSubmit={handleUpdateLimit}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Daily Email Limit
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={limitForm.value}
                    onChange={(e) => setLimitForm({ ...limitForm, value: e.target.value })}
                    required
                    className="w-full px-4 py-2 bg-black/50 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500"
                    placeholder="400"
                  />
                </div>
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowLimitModal(false);
                      setLimitForm({ value: '', type: '', id: null });
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 rounded-lg transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-all"
                  >
                    Update
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default EmailAccounts;
